/**
 * EditableTestPlanCard — the "editable plan" surface for UI/web testing.
 *
 * When the agent proposes a plan of test cases it used to be approve-or-reject.
 * Here the user (who knows the project) can steer it before it runs: toggle
 * cases off, edit a name/description, remove one, or add a case the agent
 * missed. The edited list is what the agent then generates and runs.
 */

import React, { useMemo, useState } from 'react';
import {
  Box, Button, Checkbox, IconButton, TextField, Typography, Tooltip,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, PlayArrow as RunIcon,
  Close as SkipIcon,
} from '@mui/icons-material';

export interface PlanCase {
  name: string;
  description?: string;
  steps?: any;
  _include?: boolean;
  [k: string]: any;
}

interface Props {
  testCases: PlanCase[];
  onApprove: (cases: PlanCase[]) => void;
  onSkip: () => void;
  disabled?: boolean;
}

const EditableTestPlanCard: React.FC<Props> = ({ testCases, onApprove, onSkip, disabled }) => {
  const [rows, setRows] = useState<PlanCase[]>(
    () => (testCases || []).map((tc) => ({ ...tc, _include: tc._include !== false })),
  );

  const includedCount = useMemo(
    () => rows.filter((r) => r._include && r.name.trim()).length,
    [rows],
  );

  const update = (i: number, patch: Partial<PlanCase>) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const remove = (i: number) => setRows((rs) => rs.filter((_, idx) => idx !== i));
  const add = () =>
    setRows((rs) => [...rs, { name: '', description: '', _include: true }]);

  const submit = () => {
    const out = rows
      .filter((r) => r._include && r.name.trim())
      .map(({ _include, ...tc }) => tc); // strip UI-only flag
    onApprove(out);
  };

  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        Edit the plan before it runs — untick to skip a case, edit the text, remove
        one, or add cases the agent missed.
      </Typography>

      <Box sx={{ maxHeight: 320, overflow: 'auto', mb: 1 }}>
        {rows.map((tc, i) => (
          <Box
            key={i}
            sx={{
              display: 'flex', alignItems: 'flex-start', gap: 0.5, py: 0.5,
              borderBottom: '1px solid', borderColor: 'divider',
              opacity: tc._include ? 1 : 0.5,
            }}
          >
            <Checkbox
              size="small" checked={!!tc._include} disabled={disabled}
              onChange={(e) => update(i, { _include: e.target.checked })}
              sx={{ p: 0.5, mt: 0.25 }}
            />
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <TextField
                size="small" variant="standard" placeholder="Test case name"
                value={tc.name} disabled={disabled}
                onChange={(e) => update(i, { name: e.target.value })}
                InputProps={{ sx: { fontSize: '0.85rem', fontWeight: 600 } }}
              />
              <TextField
                size="small" variant="standard" placeholder="What it checks (optional)"
                value={tc.description || ''} disabled={disabled} multiline
                onChange={(e) => update(i, { description: e.target.value })}
                InputProps={{ sx: { fontSize: '0.78rem' } }}
              />
            </Box>
            <Tooltip title="Remove">
              <IconButton size="small" onClick={() => remove(i)} disabled={disabled} sx={{ mt: 0.25 }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Button
          size="small" startIcon={<AddIcon />} onClick={add} disabled={disabled}
          sx={{ textTransform: 'none' }}
        >
          Add test case
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small" variant="outlined" startIcon={<SkipIcon />}
            onClick={onSkip} disabled={disabled} sx={{ textTransform: 'none' }}
          >
            Skip
          </Button>
          <Button
            size="small" variant="contained" startIcon={<RunIcon />}
            onClick={submit} disabled={disabled || includedCount === 0}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Run {includedCount} test{includedCount === 1 ? '' : 's'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default EditableTestPlanCard;
